import { css, cx } from '@linaria/atomic';
import { onlyText } from 'react-children-utilities';
import { TableInstance } from 'react-table';
import { theme } from '@frontend/theme';
import { Text, PrimaryButton } from '../../';
import { usePopoverMenu, PopoverMenu, PopoverMenuItem } from '../../popover';
import { emptyStateGraphics } from '../empty-states';
import { TableData } from '../table-data-type';
import { EmptyStateConfig } from '../table-types';

const emptyStatesDivStyle1 = css`
  position: sticky;
  left: 0;
  display: flex;
  align-items: center;
  padding: ${theme.spacing(5)} 0;
  flex-direction: column;
`;

const emptyStatesEmptyStateGraphicStyle2 = css`
  height: 200px;
`;

const emptyStatesDivStyle3 = css`
  margin-top: ${theme.spacing(3)};
`;

const emptyStatesTextStyle4 = css`
  font-size: ${theme.fontSize(20)};
  max-width: 400px;
`;

const emptyStatesDivStyle5 = css`
  margin-top: ${theme.spacing(1)};
`;

const emptyStatesTextStyle6 = css`
  font-size: ${theme.fontSize(16)};
  max-width: 400px;
`;

const emptyStateActionPrimaryButtonStyle7 = css`
  margin-top: ${theme.spacing(3)};
  margin-bottom: ${theme.spacing(1)};
`;

const emptyStateMultiActionsPrimaryButtonStyle8 = css`
  margin-top: ${theme.spacing(3)};
  margin-bottom: ${theme.spacing(1)};
`;

type EmptyStatesProps<T extends TableData> = {
  emptyStateConfig?: EmptyStateConfig<T>;
  tableInstance: TableInstance<T>;
};

export const EmptyStates = <T extends TableData>({ emptyStateConfig, tableInstance }: EmptyStatesProps<T>) => {
  const EmptyStateGraphic = emptyStateConfig?.type
    ? emptyStateGraphics[emptyStateConfig?.type]
    : emptyStateGraphics['generic'];
  return (
    <div className='empty__states__wrapper'>
      <div className={cx('empty__state__container', emptyStatesDivStyle1)}>
        <EmptyStateGraphic className={emptyStatesEmptyStateGraphicStyle2} />
        <div className={emptyStatesDivStyle3}>
          {typeof emptyStateConfig?.header === 'function' ? (
            emptyStateConfig?.header(tableInstance)
          ) : (
            <Text className={emptyStatesTextStyle4} textAlign='center' weight='bold' color='light'>
              {emptyStateConfig?.header ?? 'No data to display'}
            </Text>
          )}
        </div>
        <div className={emptyStatesDivStyle5}>
          {!!emptyStateConfig?.description &&
            (typeof emptyStateConfig.description === 'function' ? (
              emptyStateConfig.description(tableInstance)
            ) : (
              <Text className={emptyStatesTextStyle6} textAlign='center' color='light'>
                {emptyStateConfig?.description}
              </Text>
            ))}
        </div>
        {!!emptyStateConfig?.action && (
          <>
            {!!emptyStateConfig.action.actions ? (
              <EmptyStateMultiActions action={emptyStateConfig?.action} tableInstance={tableInstance} />
            ) : (
              <EmptyStateAction action={emptyStateConfig.action} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

type EmptyStateActionProps<T extends TableData> = {
  action: EmptyStateConfig<T>['action'];
};

const EmptyStateAction = <T extends TableData>({ action }: EmptyStateActionProps<T>) => {
  if (!action) return null;

  const { label, ...rest } = action;

  return (
    <PrimaryButton size='tiny' {...rest} className={cx(emptyStateActionPrimaryButtonStyle7, rest.className)}>
      {label}
    </PrimaryButton>
  );
};

type EmptyStateMultiActionsProps<T extends TableData> = {
  action: EmptyStateConfig<T>['action'];
  tableInstance: TableInstance<T>;
};

const EmptyStateMultiActions = <T extends TableData>({ action, tableInstance }: EmptyStateMultiActionsProps<T>) => {
  const { getTriggerProps, getMenuProps, getItemProps } = usePopoverMenu({ placement: 'top' });

  if (!action?.actions) return null;

  const { label, actions, ...rest } = action;

  const availableActions = actions.filter((action) => !action.hide);
  const hasActionsAvailable = !!availableActions.length;

  return (
    <>
      <PrimaryButton
        size='tiny'
        {...(hasActionsAvailable && getTriggerProps())}
        {...rest}
        className={cx(emptyStateMultiActionsPrimaryButtonStyle8, rest.className)}
      >
        {label}
      </PrimaryButton>
      {!!hasActionsAvailable && (
        <PopoverMenu {...getMenuProps()}>
          {availableActions.map(({ Icon, label, disabled, onClick, destructive }, index) => (
            <PopoverMenuItem
              key={onlyText(label as React.ReactNode)}
              Icon={Icon}
              {...getItemProps({
                onClick: (e) => {
                  onClick?.(e as any, tableInstance);
                },
                index,
              })}
              disabled={disabled}
              destructive={destructive}
            >
              {label}
            </PopoverMenuItem>
          ))}
        </PopoverMenu>
      )}
    </>
  );
};
